import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LegalFooter } from "@/components/LegalFooter";
import { ArrowLeft, Cookie, FileText, PawPrint, ShieldCheck, Scale } from "lucide-react";

const LAST_UPDATED = "August 1, 2026";

const DOCS = {
  privacy: {
    icon: FileText,
    title: "Privacy Policy",
    intro: "This Privacy Policy explains what information Soula Paws (\"we\", \"us\") collects, how we use it, and the choices you have. We are privacy-first by design: your data is used to run the service for you — never sold.",
    sections: [
      { h: "1. Information we collect", body: [
        "Account information: your name, email address, role (owner or caregiver), and hashed password. We never store passwords in plain text.",
        "Pet and wellness data you provide: pet profiles, daily check-ins, wellness logs, rituals, bond scores, reports, photos, and notes. This data exists solely so the service can work for you.",
        "Support data: support tickets, messages, and any files you attach.",
        "Billing data: subscription plan and transaction records. Card details are collected and stored by Stripe, our PCI DSS-compliant payment processor — Soula Paws never sees or stores your full card number.",
        "Technical data: IP address, request identifiers, and security logs used for fraud prevention, rate limiting, and audit trails.",
      ]},
      { h: "2. How we use your information", body: [
        "To provide the service: bond scores, AI insights, reports, care-circle coordination, and support.",
        "To process AI features: relevant pet context you provide may be sent to our AI model providers strictly to generate the response you requested. AI outputs are observation-based and never a diagnosis.",
        "To communicate with you: transactional emails (welcome, billing, support replies) via our email processor. We do not send third-party marketing.",
        "To secure the platform: abuse prevention, brute-force protection, rate limiting, and audit logging.",
      ]},
      { h: "3. Who we share data with", body: [
        "Service providers acting on our instructions only: Stripe (payments), Resend (transactional email), cloud infrastructure hosting, and AI model providers (for AI features you invoke).",
        "People you explicitly authorize: care-circle members you invite, and report recipients you consent to share with. Nothing is shared without your consent.",
        "Legal requirements: we may disclose information if required by law, court order, or to protect the safety of people or animals.",
        "We do not sell, rent, or trade your personal information. Ever.",
      ]},
      { h: "4. Data retention & deletion", body: [
        "We keep your data while your account is active. Records you delete are soft-deleted immediately and purged on a rolling basis.",
        "You can export a full copy of your data anytime from Privacy & Consent → Export my data.",
        "You can request full account deletion from Privacy & Consent. Deletion requests are verified by a human and confirmed with you before anything is removed.",
      ]},
      { h: "5. Your rights", body: [
        "Depending on where you live (including GDPR and CCPA/CPRA jurisdictions), you may have rights to access, correct, export, restrict, or delete your personal information, and to non-discrimination for exercising those rights.",
        "You can exercise these rights directly in the app (Privacy & Consent) or by emailing luminasoula@gmail.com. We respond within 30 days.",
      ]},
      { h: "6. Security", body: [
        "Data is encrypted in transit (TLS). Passwords are hashed with bcrypt. Access is role-based and audit-logged. See our Security & Compliance page for the full program.",
      ]},
      { h: "7. Children", body: [
        "Soula Paws is not directed to children under 13 (or under 16 in the EEA), and we do not knowingly collect their data. Accounts require users to be 18 or older.",
      ]},
      { h: "8. Changes & contact", body: [
        "We will notify you of material changes to this policy by email or in-app notice before they take effect.",
        "Contact: luminasoula@gmail.com — Soula Paws Privacy Team.",
      ]},
    ],
  },
  terms: {
    icon: Scale,
    title: "Terms of Service",
    intro: "These Terms of Service (\"Terms\") govern your use of Soula Paws. By creating an account or using the service you agree to these Terms. If you do not agree, do not use the service.",
    sections: [
      { h: "1. What Soula Paws is — and is not", body: [
        "Soula Paws is an observation-based wellness and relationship-intelligence platform for people and their animals.",
        "IMPORTANT: Soula Paws is NOT a veterinary provider, medical service, or emergency service. It does not diagnose conditions, prescribe medication, or replace professional veterinary care. AI outputs are informational suggestions based on the data you provide, carry stated confidence levels, and may be inaccurate or incomplete.",
        "If your animal shows urgent, medical, persistent, or worsening signs, contact a veterinarian or emergency clinic immediately. Never delay professional care because of anything in this app.",
      ]},
      { h: "2. Eligibility & accounts", body: [
        "You must be at least 18 years old and provide accurate registration information.",
        "You are responsible for safeguarding your password and for all activity under your account. Notify us immediately of any unauthorized use.",
      ]},
      { h: "3. Subscriptions, trials & billing", body: [
        "Soula Paws is a paid subscription service with monthly plans (currently Basic $19.99, Pro $29.99, Premium $49.99 per month, in USD, plus applicable taxes). Prices and plan features may change with prior notice.",
        "Free trials: new subscribers receive a 14-day free trial (30 days for approved beta testers). A valid payment card is required at signup; your first charge occurs when the trial ends unless you cancel before then.",
        "Subscriptions renew automatically each month until canceled. You can cancel anytime via Plan & Billing → Manage subscription; access continues through the end of the paid period. Plan changes are prorated automatically.",
        "Beta program: approved beta testers receive a 30-day Premium trial and a 10% lifetime discount. Beta features may change or be discontinued.",
        "Except where required by law, payments are non-refundable. Payments are processed by Stripe under their terms.",
      ]},
      { h: "4. Acceptable use", body: [
        "You agree not to: misuse or attempt to disrupt the service; probe or breach security measures; scrape or harvest data; upload malicious code or unlawful content; infringe others' rights; or use the service to harm any person or animal.",
        "We may suspend or terminate accounts that violate these Terms or create risk for other users, after notice where practicable.",
      ]},
      { h: "5. Your content & our IP", body: [
        "You own the content you upload (pet data, photos, notes). You grant us a limited license to store and process it solely to operate the service for you.",
        "The Soula Paws software, design, and branding are our intellectual property. No rights are granted except as needed to use the service.",
      ]},
      { h: "6. Disclaimers", body: [
        "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT AI OUTPUTS, SCORES, OR INSIGHTS ARE ACCURATE, COMPLETE, OR SUITABLE FOR ANY PARTICULAR DECISION ABOUT AN ANIMAL'S HEALTH OR CARE.",
      ]},
      { h: "7. Limitation of liability", body: [
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW, SOULA PAWS AND ITS OWNERS, EMPLOYEES, AND SUPPLIERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE — INCLUDING ANY DECISION MADE IN RELIANCE ON AI OUTPUTS.",
        "OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS BEFORE THE CLAIM AROSE.",
        "Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.",
      ]},
      { h: "8. Indemnification", body: [
        "You agree to indemnify and hold Soula Paws harmless from claims arising out of your content, your misuse of the service, or your violation of these Terms or applicable law.",
      ]},
      { h: "9. Governing law & disputes", body: [
        "These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-law rules. Disputes will be resolved in the state or federal courts located in Delaware, unless applicable consumer law provides otherwise in your place of residence.",
      ]},
      { h: "10. Changes & contact", body: [
        "We may update these Terms; material changes will be notified by email or in-app notice at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.",
        "Contact: luminasoula@gmail.com.",
      ]},
    ],
  },
  security: {
    icon: ShieldCheck,
    title: "Security & Compliance",
    intro: "Security is foundational at Soula Paws. Our security program is modeled on the SOC 2 Trust Services Criteria (security, availability, confidentiality, and privacy), and we apply HIPAA-grade safeguards to sensitive data even though pet wellness data is not regulated PHI. This page describes the controls in place today.",
    sections: [
      { h: "1. Compliance posture", body: [
        "SOC 2: our controls are designed to align with the SOC 2 Trust Services Criteria, and we are actively working toward formal SOC 2 attestation with continuous compliance monitoring.",
        "HIPAA-grade safeguards: although animal wellness data is not Protected Health Information under HIPAA, we voluntarily apply comparable administrative and technical safeguards — access controls, audit logging, encryption in transit, and least-privilege design.",
        "PCI DSS: all card payments are processed by Stripe, a certified PCI DSS Level 1 service provider. Card numbers never touch Soula Paws servers.",
        "GDPR / CCPA: we support data-subject rights (access, export, deletion) directly in-app via Privacy & Consent.",
      ]},
      { h: "2. Application security", body: [
        "Encryption in transit: all traffic is served over TLS (HTTPS) with HSTS enforced.",
        "Authentication: passwords hashed with bcrypt; short-lived signed JWT sessions; account lockout after repeated failed sign-in attempts (brute-force protection).",
        "Authorization: strict role-based access control (owner / caregiver / admin) enforced server-side on every request; users can only access their own records.",
        "Rate limiting: layered per-endpoint and global per-IP rate limits protect against abuse and denial-of-service attempts.",
        "Bot protection: invisible proof-of-work challenges on signup and password-reset stop automated abuse with zero user friction \u2014 no puzzles, no tracking, no third-party service.",
        "Hardened HTTP headers: Content-Security-Policy, X-Frame-Options (clickjacking protection), X-Content-Type-Options, Referrer-Policy, Permissions-Policy, and Cross-Origin-Opener-Policy.",
        "Upload safety: file type allow-listing and per-file size limits on all attachments.",
      ]},
      { h: "3. Data protection & auditability", body: [
        "Every sensitive action (sign-in, data changes, AI calls, billing events, admin actions) is written to an immutable audit log.",
        "Deleted records are soft-deleted with full traceability before purge, preventing accidental data loss.",
        "Payment amounts and plan entitlements are resolved server-side only — the client can never set its own price or plan.",
        "AI guardrails: every AI feature is bounded by non-negotiable safety rules — no diagnosis, no prescriptions, mandatory escalation language for urgent signals, and stated confidence levels.",
      ]},
      { h: "4. Responsible disclosure", body: [
        "We welcome good-faith security research. If you believe you've found a vulnerability, email luminasoula@gmail.com with the subject \"SECURITY\" and we will acknowledge within 48 hours. Please do not access other users' data or degrade the service while testing.",
      ]},
      { h: "5. Incident response", body: [
        "We maintain an incident response process covering triage, containment, remediation, and user notification. If an incident affects your personal data, we will notify you without undue delay, consistent with applicable law.",
      ]},
    ],
  },
  cookies: {
    icon: Cookie,
    title: "Cookie Notice",
    intro: "Soula Paws uses the minimum browser storage necessary to run the service. We do not use advertising trackers, and we do not sell data derived from cookies or local storage.",
    sections: [
      { h: "1. What we use", body: [
        "Essential session storage: a signed authentication token stored in your browser's local storage keeps you signed in securely. It is strictly necessary — the app cannot function without it.",
        "Preference storage: small values that remember interface choices on your device, including whether you have dismissed our cookie notice banner.",
        "We do not set third-party advertising or cross-site tracking cookies.",
      ]},
      { h: "2. Third-party cookies during checkout", body: [
        "When you subscribe, you are taken to Stripe's secure checkout, which sets its own cookies for fraud prevention and payment processing under Stripe's privacy policy.",
      ]},
      { h: "3. Managing storage", body: [
        "You can clear local storage and cookies anytime in your browser settings. Clearing them signs you out but never deletes your Soula Paws account data.",
        "Because we only use strictly necessary storage, no cookie-consent banner is required in most jurisdictions — but we keep this notice available for full transparency.",
      ]},
      { h: "4. Contact", body: [
        "Questions about this notice: luminasoula@gmail.com.",
      ]},
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const navigate = useNavigate();
  const data = DOCS[doc] || DOCS.privacy;
  const Icon = data.icon;
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-30 bg-[hsl(var(--background)/0.92)] backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo-link">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center"><PawPrint className="w-4 h-4 text-primary-foreground" /></div>
            <span className="font-display text-lg font-semibold">Soula Paws</span>
          </Link>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(-1)} data-testid="legal-back-button">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-10 w-full" data-testid={`legal-page-${doc}`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
          <h1 className="font-display text-3xl font-semibold" data-testid="legal-page-title">{data.title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-6">Last updated: {LAST_UPDATED}</p>
        <Card className="rounded-2xl">
          <CardContent className="p-6 md:p-8">
            <p className="text-sm leading-relaxed text-muted-foreground">{data.intro}</p>
            <div className="mt-7 space-y-7">
              {data.sections.map((s) => (
                <section key={s.h}>
                  <h2 className="font-semibold text-base mb-2.5">{s.h}</h2>
                  <div className="space-y-2">
                    {s.body.map((p, i) => (
                      <p key={i} className="text-sm leading-relaxed text-muted-foreground">{p}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </CardContent>
        </Card>
        <div className="flex flex-wrap gap-2 mt-6">
          {Object.entries(DOCS).filter(([k]) => k !== doc).map(([k, v]) => (
            <Link key={k} to={`/legal/${k}`}>
              <Button variant="outline" size="sm" className="rounded-xl" data-testid={`legal-nav-${k}`}>{v.title}</Button>
            </Link>
          ))}
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
